import { Grid, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl, useHasRole } from "../../hooks/auth/useCheckPermission";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useTransfersInvestmentBills } from "../../services/bankingService";
import TableService from "../../services/tableService";

import TransferNavigationBar from "./TransferNavigationBar";

import { useTransferTableFieldsConfig, TRANSFER_TABLE_NAME } from "./TansferInvestmentBillTableFieldsConfig";

import { InvestmentFilter } from "../../components/common/InvestmentFilter";
import { DateRangeFilter } from "../../components/common/DateRangeFilter/DateRangeFilter";

import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import InvoicingNavigationBar from "../InvoicingInvestmentBillPage/InvoicingNavigrationBar";

import { ChangeTransferIsInvoicedButton } from "./ChangeTransferIsInvoicedButton";

export default function FinancialPanelTransferInvestmentBillPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.INVESTMENT_DPI, USER_ROLES.ADMIN, USER_ROLES.ACCOUNT]);
    const hasAccessToInvoiceTransfer = props.isInvoicingPage && useHasRole([USER_ROLES.ACCOUNT, USER_ROLES.ADMIN]);
    
    if (!hasAccess){
        return null
    }

    const { t } = useTranslation();
    const TRANSFER_TABLE_FIELDS_CONFIG = useTransferTableFieldsConfig({isForInvocingPage: props.isInvoicingPage});

    const { pageName } = props;
    const TABLE_CONFIGS = [{ name: TRANSFER_TABLE_NAME, config: TRANSFER_TABLE_FIELDS_CONFIG }];

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [checkedAll, setCheckedAll] = useState(false);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: transferData, refetch, isLoading } = useTransfersInvestmentBills(searchParams);

    useEffect(() => {
        if (transferData?.results) {
            setDataLocal(transferData.results);
            setRecordsCount(transferData.count);
        }
    }, [transferData, dataPage, dataPageSize]);

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
    }, []);

    const trnasferDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            TRANSFER_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[TRANSFER_TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[TRANSFER_TABLE_NAME] || [],
        );
    }, [TRANSFER_TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                TRANSFER_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[TRANSFER_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[TRANSFER_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[TRANSFER_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[TRANSFER_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            TRANSFER_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    const getSeletedIds = () =>
    dataLocal
        .filter((rowData) => rowData.selected)
        .map((rowData) => rowData.id);

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    TRANSFER_TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[TRANSFER_TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[TRANSFER_TABLE_NAME] || [],
            )}
            checkedAll={checkedAll}
            setCheckedAll={setCheckedAll}
            showCheckbox={hasAccessToInvoiceTransfer ? true : false}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"bankings/transfers/investment_bills/export/"}
            exportToFileSearchParams={searchParams}
            exportToFileHeaders={trnasferDataExportHeaders}
            exportToFileFileName={`${t("bankings.exportTransferFileName")}.xlsx`}
            showDetailsIcon={false}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            isLoading={isLoading}
            disableRowHighlight={props.isInvoicingPage ? false : true}
        />
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={props.isInvoicingPage ? t("headers.invoicing_investment_bills") : t("headers.transfer_register")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                rowSpacing={1}
                columnSpacing={2}
            >
                <Grid item xs={12}>
                    {props.isInvoicingPage ? <InvoicingNavigationBar /> : < TransferNavigationBar />}
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        refreshTable={refetch}
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={false}
                        showMyFiltersButton={false}
                        showSearchButton={false}
                        style={{ marginBlock: "5px" }}
                        extraButtonList={[
                            <InvestmentFilter
                                filterName={"transfer_invest"}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                                InputLabelStyle={{marginTop: "-6px"}}
                                maxHeight={"40px"}
                            />,
                            <DateRangeFilter
                                labelFrom={t("bankings.agrepay_due_date_from")}
                                labelTo={t("bankings.agrepay_due_date_to")}
                                filterName={"agrepay_due_date"}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                            />,
                            hasAccessToInvoiceTransfer ? <ChangeTransferIsInvoicedButton selectedTransfers={getSeletedIds()}/> : null
                        ]}
                        extraButtonListMdGrid={[2,4, hasAccessToInvoiceTransfer ? 2 : 0]}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>{tableComponent()}</LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
